import {
  ActiveStrokeCapture,
  DEFAULT_ACTIVE_STROKE_LIMITS,
  type ActiveStrokeLimits,
} from "../history/ActiveStrokeCapture";
import {
  createAddComicObjectCommand,
  createClearPanelCommand,
  createDeleteComicObjectCommand,
  createDuplicatePanelCommand,
  createEditComicTextCommand,
  createTransformComicObjectCommand,
} from "../history/ComicCommands";
import { HistoryManager } from "../history/HistoryManager";
import { createStrokeCommand } from "../history/StrokeCommand";
import type { HistoryCommand, HistoryState, StrokeCommand } from "../history/historyTypes";
import { createThumbnailBlob } from "../persistence/thumbnail";
import {
  createClearLayerCommand,
  createLayerMutationCommand,
  createMergeLayerDownCommand,
} from "../history/LayerCommands";
import {
  clampBubbleToPanel,
  createDefaultBubble,
  duplicateBubbleToPanel,
  moveBubble,
} from "../comic/BubbleGeometry";
import { sanitizeBubbleText } from "../comic/BubbleTextLayout";
import { ComicDocumentController } from "../comic/ComicDocumentController";
import { ComicInteractionController } from "../comic/ComicInteractionController";
import { renderComicSelection, renderPanelBoundaries } from "../comic/ComicRenderer";
import { panelAtPoint, pointInRect } from "../comic/PanelGeometry";
import { cloneDocumentState } from "../comic/comicValidation";
import {
  bubbleTypeForTool,
  isDrawingStudioTool,
  isRasterStudioTool,
  MAX_COMIC_OBJECTS,
  type ComicBubbleObject,
  type ComicPanel,
  type ComicSelectionState,
  type PersistedDocumentState,
  type Rect,
  type StudioTool,
} from "../comic/comicTypes";
import { screenToDocument } from "./canvasMath";
import type {
  AccessibleComicTransform,
  AccessibleRasterTransform,
  CanvasSaveArtifacts,
  Dimensions,
  DocumentChangeReason,
  DrawingSettings,
  InteractionInterruptionReason,
  LayerPreviewBlob,
  Point,
  PointerKind,
  StrokePoint,
  ViewportTransform,
} from "./canvasTypes";
import { normalizePressure, smoothPressure } from "./pressure";
import { StrokeRenderer } from "./StrokeRenderer";
import { ViewportController } from "./ViewportController";
import { LayerController, type LayerControllerState } from "../layers/LayerController";
import { LayerRasterStore } from "../layers/LayerRasterStore";
import { compositeLayerDocument, createCompositeCanvas } from "../layers/LayerCompositor";
import type { LayerPreset, StoredRasterLayerBlob } from "../layers/layerTypes";
import type { EncodedRasterLayer } from "../layers/RasterLayerSurface";
import { canvasToBlob } from "../persistence/artworkCodec";
import { RasterSelectionController } from "../selection/RasterSelectionController";
import {
  createRasterSelectionCommand,
  drawRasterSelectionSource,
} from "../selection/RasterSelectionCommand";
import {
  createRasterPixelCanvas,
  type ImmutableRasterPixelBuffer,
} from "../selection/RasterPixelBuffer";
import { renderRasterSelectionOverlay } from "../selection/SelectionRenderer";
import {
  flipSelectionTransform,
  integerConstraintRect,
  moveSelectionTransform,
  rectToSelectionTransform,
  transformsDiffer,
} from "../selection/selectionGeometry";
import { selectionFitsMemoryLimits, selectionRegionBytes } from "../selection/selectionMemory";
import type {
  RasterSelectionAction,
  RasterSelectionTransform,
  RasterSelectionUiState,
} from "../selection/selectionTypes";
import { ShapeInteractionController } from "../shapes/ShapeInteractionController";
import { createShapeCommand } from "../shapes/ShapeCommand";
import { renderShape } from "../shapes/ShapeRenderer";
import { isShapeTool, type ShapeMode } from "../shapes/shapeTypes";
import { createFillCommand } from "../fill/FillCommand";
import type { FillTolerance } from "../fill/fillTypes";
import { rgbaToHex } from "../colour/colourMath";
import { renderGuideOverlay } from "../activities/GuideRenderer";
import type { GuideOverlayState } from "../activities/activityTypes";

type ZoomListener = (zoomPercent: number) => void;
type DocumentChangeListener = (reason: DocumentChangeReason) => void;
type HistoryListener = (state: HistoryState) => void;
type ComicSelectionListener = (selection: ComicSelectionState) => void;
type RasterSelectionListener = (selection: RasterSelectionUiState) => void;
type TextEditListener = (object: ComicBubbleObject) => void;
type LayerListener = (state: LayerControllerState) => void;
type InteractionErrorListener = (message: string) => void;
type ColourPickListener = (colour: string) => void;

interface PendingBubbleAdd {
  type: ComicBubbleObject["type"];
  panelId: string;
  point: Point;
}

type PendingRasterTap =
  | {
      tool: "pickColour";
      point: Point;
      clipRect: Rect;
      isPointerInsideClip: boolean;
    }
  | {
      tool: "fill";
      layerId: string;
      point: Point;
      clipRect: Rect;
      isPointerInsideClip: boolean;
      panelId?: string;
    };

function cloneObject(object: Readonly<ComicBubbleObject>): ComicBubbleObject {
  return { ...object, data: { ...object.data } };
}

export class DrawingEngine {
  private readonly raster: LayerRasterStore;
  private readonly baselineRaster: LayerRasterStore;
  private readonly layers: LayerController;
  private readonly strokeRenderer: StrokeRenderer;
  private readonly viewport: ViewportController;
  private readonly history: HistoryManager;
  private readonly activeStrokeCapture: ActiveStrokeCapture;
  private readonly comicDocument: ComicDocumentController;
  private readonly comicInteraction = new ComicInteractionController();
  private readonly rasterSelection = new RasterSelectionController();
  private readonly shapeInteraction = new ShapeInteractionController();
  private baselineObjects: ComicBubbleObject[];
  private baselineLayerState: LayerControllerState;
  private settings: DrawingSettings;
  private studioTool: StudioTool;
  private displayWidth = 1;
  private displayHeight = 1;
  private displayDpr = 1;
  private animationFrame: number | null = null;
  private strokeActive = false;
  private activeStrokeSettings: DrawingSettings | null = null;
  private activeStrokePanel: ComicPanel | null = null;
  private activeStrokeLayerId: string | null = null;
  private activeLayerPreviewCanvas: HTMLCanvasElement | null = null;
  private activeRasterSelectionPixels: ImmutableRasterPixelBuffer | null = null;
  private activeRasterSelectionSourceCanvas: HTMLCanvasElement | null = null;
  private pendingBubbleAdd: PendingBubbleAdd | null = null;
  private pendingRasterTap: PendingRasterTap | null = null;
  private shapeMode: ShapeMode = "outline";
  private fillTolerance: FillTolerance = 24;
  private guideOverlay: GuideOverlayState | null = null;
  private smoothedPressure = 0.65;
  private destroyed = false;
  private lastReportedZoom = -1;

  public constructor(
    private readonly displayCanvas: HTMLCanvasElement,
    private readonly documentSize: Dimensions,
    settings: DrawingSettings,
    studioTool: StudioTool,
    initialDocumentState: PersistedDocumentState,
    _dialogueLayerId: string | undefined,
    private readonly onZoomChange: ZoomListener,
    private readonly onDocumentChange: DocumentChangeListener = () => undefined,
    private readonly onHistoryChange: HistoryListener = () => undefined,
    private readonly onSelectionChange: ComicSelectionListener = () => undefined,
    private readonly onTextEditRequest: TextEditListener = () => undefined,
    activeStrokeLimits: Readonly<ActiveStrokeLimits> = DEFAULT_ACTIVE_STROKE_LIMITS,
    private readonly onLayerChange: LayerListener = () => undefined,
    private readonly onInteractionError: InteractionErrorListener = () => undefined,
    private readonly onRasterSelectionChange: RasterSelectionListener = () => undefined,
    private readonly onColourPick: ColourPickListener = () => undefined,
  ) {
    this.settings = { ...settings };
    this.studioTool = studioTool;
    const strokeCanvas = document.createElement("canvas");
    strokeCanvas.width = documentSize.width;
    strokeCanvas.height = documentSize.height;
    this.strokeRenderer = new StrokeRenderer(strokeCanvas);
    this.viewport = new ViewportController(documentSize);
    this.comicDocument = new ComicDocumentController(cloneDocumentState(initialDocumentState));
    this.layers = new LayerController(
      { activeLayerId: initialDocumentState.activeLayerId, layers: initialDocumentState.layers },
      initialDocumentState.comicTemplateId ? "comic" : "drawing",
    );
    this.raster = new LayerRasterStore(documentSize, initialDocumentState.layers);
    this.baselineRaster = this.raster.clone();
    this.baselineObjects = initialDocumentState.objects.map(cloneObject);
    this.baselineLayerState = this.layers.getSnapshot();
    this.history = new HistoryManager(undefined, (commands) => this.bakeIntoBaseline(commands));
    this.activeStrokeCapture = new ActiveStrokeCapture(activeStrokeLimits);
    this.reportHistory();
    this.reportSelection();
    this.reportRasterSelection();
    this.reportLayers();
  }

  public resize(width: number, height: number, devicePixelRatio: number): void {
    if (this.destroyed) return;
    this.displayWidth = Math.max(1, width);
    this.displayHeight = Math.max(1, height);
    this.displayDpr = Math.min(3, Math.max(1, devicePixelRatio));
    this.displayCanvas.width = Math.max(1, Math.round(this.displayWidth * this.displayDpr));
    this.displayCanvas.height = Math.max(1, Math.round(this.displayHeight * this.displayDpr));
    this.viewport.resize(this.displayWidth, this.displayHeight);
    this.reportZoom();
    this.requestRender();
  }

  public updateSettings(settings: DrawingSettings): void {
    const changed =
      settings.tool !== this.settings.tool ||
      settings.colour !== this.settings.colour ||
      settings.size !== this.settings.size ||
      settings.opacity !== this.settings.opacity ||
      settings.pressureEnabled !== this.settings.pressureEnabled;
    if (changed && this.strokeActive) this.finishStroke();
    this.settings = { ...settings };
  }

  public updateGuideOverlay(overlay: Readonly<GuideOverlayState> | null): void {
    this.guideOverlay = overlay ? { ...overlay } : null;
    this.requestRender();
  }

  public updateRasterToolOptions(shapeMode: ShapeMode, fillTolerance: FillTolerance): void {
    this.shapeMode = shapeMode;
    this.fillTolerance = fillTolerance;
  }

  public updateStudioTool(tool: StudioTool): void {
    if (tool === this.studioTool) return;
    if (this.studioTool === "selectRasterArt") {
      this.cancelRasterSelection(true);
    } else {
      this.finishActiveInteraction();
    }
    this.studioTool = tool;
    const active = this.layers.getActive();
    let switchedLayer = false;
    if (
      tool !== "pickColour" &&
      isRasterStudioTool(tool) &&
      (active.type !== "raster" || !active.visible || active.locked)
    ) {
      const rasterLayer = this.layers
        .getSnapshot()
        .layers.find((layer) => layer.type === "raster" && layer.visible && !layer.locked);
      if (rasterLayer) switchedLayer = this.layers.activate(rasterLayer.id);
    } else if (
      tool !== "pickColour" &&
      !isRasterStudioTool(tool) &&
      (active.type !== "object" || !active.visible || active.locked)
    ) {
      const objectLayer = this.layers
        .getSnapshot()
        .layers.find((layer) => layer.type === "object" && layer.visible && !layer.locked);
      if (objectLayer) switchedLayer = this.layers.activate(objectLayer.id);
    }
    if (tool === "selectRasterArt") {
      this.comicDocument.selectObject(null);
      this.reportSelection();
    }
    this.syncDocumentLayers();
    this.reportLayers();
    if (switchedLayer) this.onDocumentChange("activeLayer");
    this.requestRender();
  }

  /**
   * Settles ephemeral Full-only state without changing pixels, history, layer
   * metadata, or persistence generation. Setting the engine tool here also
   * prevents the following React tool update from auto-activating a layer.
   */
  public revokeFullToolAccess(): void {
    this.cancelRasterSelection(true);
    this.comicInteraction.cancel();
    this.pendingBubbleAdd = null;
    this.comicDocument.selectObject(null);
    this.studioTool = "pencil";
    this.reportSelection();
    this.requestRender();
  }

  public beginPointerInteraction(
    screenPoint: Point,
    pointerType: PointerKind,
    rawPressure: number,
  ): boolean {
    if (this.destroyed || this.hasActiveInteraction()) return false;
    const point = screenToDocument(screenPoint, this.viewport.getTransform());

    if (this.studioTool === "selectRasterArt") {
      return this.beginRasterSelectionPointer(point);
    }

    if (!this.isInsideDocument(point)) {
      if (this.studioTool === "pickColour") {
        this.onInteractionError("There is no visible colour here to pick.");
      }
      return false;
    }

    if (this.studioTool === "pickColour") {
      // The picker samples the committed pre-overlay composite and is entirely
      // independent of active-layer editability. In particular, do not run the
      // tool-driven raster activation path: picking while Dialogue is active or
      // while every raster layer is hidden/locked must remain read-only.
      this.pendingRasterTap = {
        tool: "pickColour",
        point: { ...point },
        isPointerInsideClip: true,
        clipRect: {
          x: 0,
          y: 0,
          width: this.documentSize.width,
          height: this.documentSize.height,
        },
      };
      return true;
    }

    if (isShapeTool(this.studioTool) || this.studioTool === "fill") {
      if (!this.ensureEditableRasterLayer()) {
        this.onInteractionError("Choose a visible, unlocked drawing layer before using this tool.");
        return false;
      }
      const activeLayerId = this.layers.getActive().id;
      const selectedPanel = this.comicDocument.getSelectedPanel();
      if (this.comicDocument.getPanels().length > 0) {
        const touchedPanel = panelAtPoint(this.comicDocument.getPanels(), point);
        if (!touchedPanel) return false;
        if (selectedPanel?.id !== touchedPanel.id) {
          this.comicDocument.selectPanel(touchedPanel.id);
          this.comicDocument.selectObject(null);
          this.reportSelection();
          this.requestRender();
          return false;
        }
      }
      const clipRect = selectedPanel
        ? this.panelRect(selectedPanel)
        : { x: 0, y: 0, width: this.documentSize.width, height: this.documentSize.height };
      if (isShapeTool(this.studioTool)) {
        if (
          !this.raster.canAllocate(activeLayerId) ||
          !this.raster.canBeginStrokeWithPreview(activeLayerId)
        ) {
          this.onInteractionError(
            "This tablet is low on drawing memory. Merge or clear a layer and try again.",
          );
          return false;
        }
        try {
          // Shapes reuse the one full-document active-layer preview already
          // budgeted for strokes. Allocate it only after the shared preflight.
          this.ensureActiveLayerPreviewCanvas();
          this.shapeInteraction.begin(
            this.studioTool,
            activeLayerId,
            point,
            this.viewport.getTransform().zoom,
            {
              colour: this.settings.colour,
              size: this.settings.size,
              opacity: this.settings.opacity,
              mode: this.shapeMode,
            },
            clipRect,
            selectedPanel?.id,
          );
          return true;
        } catch {
          this.releaseActiveLayerPreview();
          this.onInteractionError("That shape could not be started safely. Please try again.");
          return false;
        }
      }
      if (this.studioTool === "fill") {
        const fillPixels = Math.floor(clipRect.width) * Math.floor(clipRect.height);
        if (!this.raster.canApplyFill(activeLayerId, fillPixels)) {
          this.onInteractionError(
            "This area is too large to fill safely on this tablet. Try a smaller panel.",
          );
          return false;
        }
      }
      this.pendingRasterTap = {
        tool: this.studioTool,
        layerId: activeLayerId,
        point: { ...point },
        clipRect: { ...clipRect },
        isPointerInsideClip: true,
        ...(selectedPanel ? { panelId: selectedPanel.id } : {}),
      };
      return true;
    }

    if (isDrawingStudioTool(this.studioTool)) {
      if (!this.layers.canDraw()) {
        this.onInteractionError("Choose a visible, unlocked drawing layer before drawing.");
        return false;
      }
      if (!this.raster.canAllocate(this.layers.getActive().id)) {
        this.onInteractionError(
          "This tablet is low on drawing memory. Merge or clear a layer and try again.",
        );
        return false;
      }
      // This second, shared-helper preflight covers the live full-document
      // active-layer preview. It runs before any active-stroke state or preview
      // canvas allocation. Blank targets are checked as separate live/commit
      // peaks because finishStroke releases the preview before allocating them.
      if (!this.raster.canBeginStrokeWithPreview(this.layers.getActive().id)) {
        this.onInteractionError(
          "This tablet is low on drawing memory. Merge or clear a layer and try again.",
        );
        return false;
      }
      const selectedPanel = this.comicDocument.getSelectedPanel();
      if (this.comicDocument.getPanels().length > 0) {
        const touchedPanel = panelAtPoint(this.comicDocument.getPanels(), point);
        if (!touchedPanel) return false;
        if (selectedPanel?.id !== touchedPanel.id) {
          this.comicDocument.selectPanel(touchedPanel.id);
          this.comicDocument.selectObject(null);
          this.reportSelection();
          this.requestRender();
          return false;
        }
      }
      return this.beginStrokeAtDocumentPoint(point, pointerType, rawPressure, selectedPanel);
    }

    if (!this.layers.canEditObjects()) {
      this.onInteractionError("Choose the visible, unlocked Dialogue layer to edit bubbles.");
      return false;
    }

    if (this.studioTool !== "selectComicObject") {
      const panel = this.comicDocument.getSelectedPanel();
      if (!panel || !pointInRect(point, panel)) {
        const touched = this.comicDocument.selectPanelAt(point);
        if (touched) this.reportSelection();
        this.requestRender();
        return false;
      }
      this.pendingBubbleAdd = {
        type: bubbleTypeForTool(this.studioTool),
        panelId: panel.id,
        point,
      };
      return true;
    }

    const hit = this.comicDocument.selectObjectAt(
      point,
      22 / Math.max(this.viewport.getTransform().zoom, 0.05),
    );
    if (hit) {
      const panel = this.comicDocument
        .getPanels()
        .find((candidate) => candidate.id === hit.object.panelId);
      if (panel) {
        this.comicInteraction.begin(
          hit.object,
          panel,
          hit.region,
          point,
          this.viewport.getTransform().zoom,
        );
      }
      this.reportSelection();
      this.requestRender();
      return Boolean(panel);
    }
    this.comicDocument.selectPanelAt(point);
    this.reportSelection();
    this.requestRender();
    return false;
  }

  public continuePointerInteraction(
    screenPoint: Point,
    pointerType: PointerKind,
    rawPressure: number,
  ): void {
    if (this.destroyed) return;
    if (this.strokeActive) {
      this.continueStroke(screenPoint, pointerType, rawPressure);
      return;
    }
    if (this.shapeInteraction.isActive()) {
      if (
        this.shapeInteraction.update(screenToDocument(screenPoint, this.viewport.getTransform()))
      ) {
        this.requestRender();
      }
      return;
    }
    if (this.pendingRasterTap) {
      const point = screenToDocument(screenPoint, this.viewport.getTransform());
      this.pendingRasterTap.isPointerInsideClip = pointInRect(
        point,
        this.pendingRasterTap.clipRect,
      );
      if (this.pendingRasterTap.isPointerInsideClip) {
        this.pendingRasterTap.point = { ...point };
      }
      return;
    }
    if (this.rasterSelection.isActive()) {
      if (
        this.rasterSelection.update(screenToDocument(screenPoint, this.viewport.getTransform()))
      ) {
        this.requestRender();
      }
      return;
    }
    if (this.comicInteraction.isActive()) {
      if (
        this.comicInteraction.update(screenToDocument(screenPoint, this.viewport.getTransform()))
      ) {
        this.requestRender();
      }
    }
  }

  public finishPointerInteraction(): void {
    if (this.strokeActive) {
      this.finishStroke();
      return;
    }
    if (this.rasterSelection.isActive()) {
      this.finishRasterSelectionInteraction();
      return;
    }
    if (this.shapeInteraction.isActive()) {
      const shape = this.shapeInteraction.finish();
      this.releaseActiveLayerPreview();
      if (shape) this.executeCommand(createShapeCommand(shape));
      this.requestRender();
      return;
    }
    if (this.pendingRasterTap) {
      this.finishPendingRasterTap();
      return;
    }
    const completed = this.comicInteraction.finish();
    if (completed) {
      this.executeCommand(createTransformComicObjectCommand(completed.before, completed.after));
      this.comicDocument.selectObject(completed.after.id);
      this.reportSelection();
      return;
    }
    if (this.pendingBubbleAdd) {
      const pending = this.pendingBubbleAdd;
      this.pendingBubbleAdd = null;
      const panel = this.comicDocument
        .getPanels()
        .find((candidate) => candidate.id === pending.panelId);
      if (
        panel &&
        this.layers.getActive().type === "object" &&
        this.comicDocument.getObjects().length < MAX_COMIC_OBJECTS
      ) {
        const nextOrder =
          Math.max(-1, ...this.comicDocument.getObjects().map((item) => item.order)) + 1;
        const object = createDefaultBubble(
          pending.type,
          panel,
          pending.point,
          this.layers.getActive().id,
          nextOrder,
        );
        this.executeCommand(createAddComicObjectCommand(object));
        this.comicDocument.selectObject(object.id);
        this.reportSelection();
        this.onTextEditRequest(cloneObject(object));
      }
    }
    this.requestRender();
  }

  public cancelPointerInteraction(): void {
    if (this.strokeActive) {
      // Preserve valid samples already received, but never append cancellation
      // coordinates. This retains Milestone 2 cancellation safety.
      this.finishStroke();
      return;
    }
    if (this.rasterSelection.isActive()) {
      this.rasterSelection.cancelInteraction();
      this.releaseRasterSelectionPreviewResources();
      this.reportRasterSelection();
      this.requestRender();
      return;
    }
    if (this.shapeInteraction.isActive()) {
      this.shapeInteraction.cancel();
      this.releaseActiveLayerPreview();
      this.requestRender();
      return;
    }
    if (this.studioTool === "selectRasterArt") {
      this.cancelRasterSelection(true);
      return;
    }
    this.pendingRasterTap = null;
    this.pendingBubbleAdd = null;
    this.comicInteraction.cancel();
    this.reportSelection();
    this.requestRender();
  }

  public discardPointerInteraction(): void {
    if (this.strokeActive) this.discardStroke();
    this.cancelActiveRasterSelectionInteraction();
    this.shapeInteraction.cancel();
    this.releaseActiveLayerPreview();
    this.pendingRasterTap = null;
    this.pendingBubbleAdd = null;
    this.comicInteraction.cancel();
    this.requestRender();
  }

  public finishActiveInteraction(): void {
    if (
      this.strokeActive ||
      this.comicInteraction.isActive() ||
      this.rasterSelection.isActive() ||
      this.shapeInteraction.isActive()
    )
      this.finishPointerInteraction();
    else {
      this.pendingRasterTap = null;
      this.pendingBubbleAdd = null;
    }
  }

  public interruptActiveInteraction(reason: InteractionInterruptionReason): void {
    void reason;
    if (this.strokeActive) {
      // Lifecycle interruption preserves valid samples already received under
      // the established stroke-cancellation policy, without adding a new
      // interruption coordinate.
      this.finishStroke();
      return;
    }
    if (this.cancelActiveRasterSelectionInteraction()) return;
    if (this.shapeInteraction.isActive()) {
      this.shapeInteraction.cancel();
      this.releaseActiveLayerPreview();
      this.requestRender();
      return;
    }
    if (this.comicInteraction.isActive()) {
      this.comicInteraction.cancel();
      this.reportSelection();
      this.requestRender();
      return;
    }
    this.pendingRasterTap = null;
    this.pendingBubbleAdd = null;
  }

  public hasActiveInteraction(): boolean {
    return (
      this.strokeActive ||
      this.comicInteraction.isActive() ||
      this.rasterSelection.isActive() ||
      this.shapeInteraction.isActive() ||
      this.pendingRasterTap !== null ||
      this.pendingBubbleAdd !== null
    );
  }

  public beginStroke(screenPoint: Point, pointerType: PointerKind, rawPressure: number): boolean {
    return this.beginPointerInteraction(screenPoint, pointerType, rawPressure);
  }

  public continueStroke(screenPoint: Point, pointerType: PointerKind, rawPressure: number): void {
    if (!this.strokeActive || this.destroyed) return;
    const point = screenToDocument(screenPoint, this.viewport.getTransform());
    const nextPressure = normalizePressure(rawPressure, pointerType, this.settings.pressureEnabled);
    this.smoothedPressure = smoothPressure(this.smoothedPressure, nextPressure);
    const strokePoint: StrokePoint = {
      x: Math.min(this.documentSize.width, Math.max(0, point.x)),
      y: Math.min(this.documentSize.height, Math.max(0, point.y)),
      pressure: this.smoothedPressure,
    };
    this.activeStrokeCapture.add(strokePoint);
    this.strokeRenderer.add(strokePoint);
    this.requestRender();
  }

  public finishStroke(): void {
    if (!this.strokeActive) return;
    const settings = this.activeStrokeSettings;
    const panel = this.activeStrokePanel;
    const layerId = this.activeStrokeLayerId;
    const points = this.activeStrokeCapture.finish();
    this.strokeRenderer.clear();
    this.releaseActiveLayerPreview();
    this.strokeActive = false;
    this.activeStrokeSettings = null;
    this.activeStrokePanel = null;
    this.activeStrokeLayerId = null;
    this.activeStrokeCapture.reset();
    if (settings && layerId && points.length > 0) {
      const command = createStrokeCommand(
        layerId,
        settings,
        points,
        panel ? { panelId: panel.id, clipRect: this.panelRect(panel) } : undefined,
      );
      this.executeCommand(command);
    }
    this.requestRender();
  }

  public discardStroke(): void {
    if (!this.strokeActive) return;
    this.strokeRenderer.clear();
    this.releaseActiveLayerPreview();
    this.strokeActive = false;
    this.activeStrokeSettings = null;
    this.activeStrokePanel = null;
    this.activeStrokeLayerId = null;
    this.activeStrokeCapture.reset();
    this.requestRender();
  }

  public isStrokeActive(): boolean {
    return this.strokeActive;
  }

  public undo(): boolean {
    this.cancelRasterSelection(true);
    this.finishActiveInteraction();
    if (!this.history.undo()) return false;
    this.rebuildDocument();
    this.reportHistory();
    this.reportSelection();
    this.onDocumentChange("undo");
    return true;
  }

  public redo(): boolean {
    this.cancelRasterSelection(true);
    this.finishActiveInteraction();
    if (!this.history.redo()) return false;
    this.rebuildDocument();
    this.reportHistory();
    this.reportSelection();
    this.onDocumentChange("redo");
    return true;
  }

  public selectPanel(panelId: string): void {
    this.cancelRasterSelection(true);
    this.finishActiveInteraction();
    if (this.comicDocument.selectPanel(panelId)) {
      this.reportSelection();
      this.requestRender();
    }
  }

  public clearSelectedPanel(): boolean {
    this.cancelRasterSelection(true);
    this.finishActiveInteraction();
    const panel = this.comicDocument.getSelectedPanel();
    if (!panel) return false;
    this.executeCommand(createClearPanelCommand(panel.id));
    this.comicDocument.selectObject(null);
    this.reportSelection();
    return true;
  }

  public duplicateSelectedPanel(targetPanelId: string): boolean {
    this.cancelRasterSelection(true);
    this.finishActiveInteraction();
    const source = this.comicDocument.getSelectedPanel();
    const target = this.comicDocument
      .getPanels()
      .find((panel) => panel.id === targetPanelId && panel.id !== source?.id);
    if (!source || !target) return false;
    const currentObjects = this.comicDocument.getObjects();
    const sourceObjects = currentObjects.filter((object) => object.panelId === source.id);
    const targetObjectCount = currentObjects.filter(
      (object) => object.panelId === target.id,
    ).length;
    if (currentObjects.length - targetObjectCount + sourceObjects.length > MAX_COMIC_OBJECTS) {
      return false;
    }
    const objects = sourceObjects.map((object, index) =>
      duplicateBubbleToPanel(object, source, target, crypto.randomUUID(), index),
    );
    this.executeCommand(createDuplicatePanelCommand(source.id, target.id, objects));
    this.comicDocument.selectPanel(target.id);
    this.comicDocument.selectObject(null);
    this.reportSelection();
    return true;
  }

  public deleteSelectedComicObject(): boolean {
    this.finishActiveInteraction();
    const object = this.comicDocument.getSelectedObject();
    if (!object || !this.layers.canEditObjects()) return false;
    this.executeCommand(createDeleteComicObjectCommand(object));
    this.comicDocument.selectObject(null);
    this.reportSelection();
    return true;
  }

  public updateSelectedComicText(text: string): boolean {
    this.finishActiveInteraction();
    const object = this.comicDocument.getSelectedObject();
    if (!object || !this.layers.canEditObjects()) return false;
    const sanitized = sanitizeBubbleText(text);
    if (sanitized === object.data.text) return false;
    this.executeCommand(createEditComicTextCommand(object.id, object.data.text, sanitized));
    this.reportSelection();
    return true;
  }

  public transformSelectedComicObject(change: AccessibleComicTransform): boolean {
    this.finishActiveInteraction();
    const object = this.comicDocument.getSelectedObject();
    const panel = this.comicDocument.getSelectedPanel();
    if (!object || !panel || !this.layers.canEditObjects()) return false;
    const large = change.endsWith("-large");
    const step = large ? 24 : 6;
    let after = cloneObject(object);
    if (change.startsWith("nudge-left")) after = moveBubble(after, { x: -step, y: 0 }, panel);
    else if (change.startsWith("nudge-right")) after = moveBubble(after, { x: step, y: 0 }, panel);
    else if (change.startsWith("nudge-up")) after = moveBubble(after, { x: 0, y: -step }, panel);
    else if (change.startsWith("nudge-down")) after = moveBubble(after, { x: 0, y: step }, panel);
    else if (change === "grow" || change === "shrink") {
      const factor = change === "grow" ? 1.1 : 0.9;
      after = clampBubbleToPanel(
        {
          ...after,
          x: after.x - (after.width * factor - after.width) / 2,
          y: after.y - (after.height * factor - after.height) / 2,
          width: after.width * factor,
          height: after.height * factor,
        },
        panel,
      );
    } else if (change === "rotate-left" || change === "rotate-right") {
      after = clampBubbleToPanel(
        { ...after, rotation: after.rotation + (change === "rotate-left" ? -10 : 10) },
        panel,
      );
    }
    const delta =
      Math.abs(after.x - object.x) +
      Math.abs(after.y - object.y) +
      Math.abs(after.width - object.width) +
      Math.abs(after.height - object.height) +
      Math.abs(after.rotation - object.rotation);
    if (delta < 0.01) return false;
    this.executeCommand(createTransformComicObjectCommand(object, after));
    this.comicDocument.selectObject(after.id);
    this.reportSelection();
    return true;
  }

  public applyRasterSelection(): boolean {
    if (!this.rasterSelection.isActive()) return false;
    this.finishRasterSelectionInteraction();
    return true;
  }

  public clearRasterSelection(): boolean {
    return this.cancelRasterSelection(true);
  }

  public duplicateRasterSelection(): boolean {
    const selection = this.rasterSelection.getSelection();
    if (!selection) return false;
    const original = rectToSelectionTransform(selection.sourceRect);
    const childFriendlyOffset = Math.max(24, 36 / this.viewport.getTransform().zoom);
    let destination = moveSelectionTransform(
      original,
      { x: childFriendlyOffset, y: childFriendlyOffset },
      selection.clipRect,
    );
    if (!transformsDiffer(original, destination)) {
      destination = moveSelectionTransform(
        original,
        { x: -childFriendlyOffset, y: -childFriendlyOffset },
        selection.clipRect,
      );
    }
    if (!transformsDiffer(original, destination)) return false;
    return this.commitRasterSelectionCommand("duplicate", destination, false);
  }

  public flipRasterSelection(axis: "horizontal" | "vertical"): boolean {
    const selection = this.rasterSelection.getSelection();
    if (!selection) return false;
    const destination = flipSelectionTransform(
      rectToSelectionTransform(selection.sourceRect),
      axis,
    );
    return this.commitRasterSelectionCommand(
      axis === "horizontal" ? "flipHorizontal" : "flipVertical",
      destination,
      true,
    );
  }

  public deleteRasterSelection(): boolean {
    if (!this.rasterSelection.getSelection()) return false;
    const changed = this.commitRasterSelectionCommand("delete", undefined, true);
    if (changed) {
      this.rasterSelection.clear();
      this.reportRasterSelection();
      this.requestRender();
    }
    return changed;
  }

  public transformRasterSelection(change: AccessibleRasterTransform): boolean {
    const selection = this.rasterSelection.getSelection();
    if (!selection) return false;
    const large = change.endsWith("-large");
    const step = large ? 24 : 6;
    const delta = { x: 0, y: 0 };
    if (change.startsWith("nudge-left")) delta.x = -step;
    else if (change.startsWith("nudge-right")) delta.x = step;
    else if (change.startsWith("nudge-up")) delta.y = -step;
    else if (change.startsWith("nudge-down")) delta.y = step;
    const original = rectToSelectionTransform(selection.sourceRect);
    const destination = moveSelectionTransform(original, delta, selection.clipRect);
    if (!transformsDiffer(original, destination)) return false;
    return this.commitRasterSelectionCommand("move", destination, true);
  }

  public async loadArtwork(blob: Blob): Promise<void> {
    const sketch = this.layers.getSnapshot().layers.find((layer) => layer.type === "raster");
    if (!sketch) throw new Error("A drawing layer is required.");
    await this.loadRasterLayers([
      { layerId: sketch.id, mimeType: "image/png", blank: false, blob },
    ]);
  }

  public async loadRasterLayers(records: ReadonlyArray<StoredRasterLayerBlob>): Promise<void> {
    if (this.destroyed) return;
    this.cancelRasterSelection(true);
    await this.raster.load(records);
    this.baselineRaster.replaceWith(this.raster);
    this.baselineObjects = this.comicDocument.getObjects().map(cloneObject);
    this.baselineLayerState = this.layers.getSnapshot();
    this.history.clear();
    this.reportHistory();
    this.reportSelection();
    this.reportRasterSelection();
    this.requestRender();
  }

  public async exportSaveArtifacts(): Promise<CanvasSaveArtifacts> {
    // Autosave may begin while the child is already drawing the next stroke.
    // Active stroke/transform previews live outside committed surfaces, so this
    // captures only completed actions without interrupting pointer input. Manual
    // Save and navigation explicitly finalize interactions before calling here.
    const documentState = this.getDocumentState();
    const rasterLayers = await this.raster.encode(documentState.layers);
    const composite = createCompositeCanvas(this.raster, documentState, this.documentSize);
    try {
      const thumbnailBlob = await createThumbnailBlob(composite);
      return { rasterLayers, thumbnailBlob, documentState };
    } finally {
      composite.width = 1;
      composite.height = 1;
    }
  }

  public acknowledgeSavedLayers(records: ReadonlyArray<EncodedRasterLayer>): void {
    this.raster.acknowledge(records);
  }

  public async exportLayerPreviews(): Promise<LayerPreviewBlob[]> {
    const state = this.getDocumentState();
    const scale = Math.min(1, 96 / Math.max(this.documentSize.width, this.documentSize.height));
    const previews: LayerPreviewBlob[] = [];
    for (const layer of state.layers) {
      const hasRaster = layer.type === "raster" && Boolean(this.raster.get(layer.id)?.getCanvas());
      const hasObjects =
        layer.type === "object" && state.objects.some((object) => object.layerId === layer.id);
      if (!hasRaster && !hasObjects) {
        previews.push({ layerId: layer.id });
        continue;
      }
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(this.documentSize.width * scale));
      canvas.height = Math.max(1, Math.round(this.documentSize.height * scale));
      const context = canvas.getContext("2d");
      if (!context) throw new Error("Canvas 2D is unavailable for layer previews.");
      context.scale(scale, scale);
      compositeLayerDocument(
        context,
        this.raster,
        {
          layers: state.layers.map((candidate) => ({
            ...candidate,
            visible: candidate.id === layer.id,
            opacity: candidate.id === layer.id ? 1 : candidate.opacity,
          })),
          objects: state.objects,
          panels: state.panels,
        },
        this.documentSize,
        false,
      );
      try {
        previews.push({ layerId: layer.id, blob: await canvasToBlob(canvas, "image/png") });
      } finally {
        canvas.width = 1;
        canvas.height = 1;
      }
    }
    return previews;
  }

  public activateLayer(layerId: string): void {
    this.cancelRasterSelection(true);
    this.finishActiveInteraction();
    if (this.layers.activate(layerId)) {
      this.syncDocumentLayers();
      this.reportLayers();
      this.onDocumentChange("activeLayer");
      this.requestRender();
    }
  }

  public addLayer(preset: LayerPreset): boolean {
    return this.mutateLayers("addLayer", () => this.layers.add(preset));
  }

  public renameLayer(layerId: string, name: string): boolean {
    return this.mutateLayers("renameLayer", () => this.layers.rename(layerId, name));
  }

  public setLayerVisible(layerId: string, visible: boolean): boolean {
    return this.mutateLayers("visibilityLayer", () => this.layers.setVisible(layerId, visible));
  }

  public setLayerLocked(layerId: string, locked: boolean): boolean {
    return this.mutateLayers("lockLayer", () => this.layers.setLocked(layerId, locked));
  }

  public setLayerOpacity(layerId: string, opacity: number): boolean {
    return this.mutateLayers("opacityLayer", () => this.layers.setOpacity(layerId, opacity));
  }

  public moveLayer(layerId: string, direction: -1 | 1): boolean {
    return this.mutateLayers("reorderLayer", () => this.layers.move(layerId, direction));
  }

  public deleteLayer(layerId: string): boolean {
    return this.mutateLayers("deleteLayer", () => this.layers.delete(layerId));
  }

  public clearLayer(layerId: string): boolean {
    this.cancelRasterSelection(true);
    this.finishActiveInteraction();
    const layer = this.layers.getSnapshot().layers.find((candidate) => candidate.id === layerId);
    if (!layer || layer.type !== "raster" || layer.locked || !this.raster.get(layerId)?.getCanvas())
      return false;
    this.executeCommand(createClearLayerCommand(layerId));
    return true;
  }

  public mergeLayerDown(layerId: string): boolean {
    this.cancelRasterSelection(true);
    this.finishActiveInteraction();
    const before = this.layers.getSnapshot();
    const source = before.layers.find((layer) => layer.id === layerId);
    const target = this.layers.getMergeTarget(layerId);
    if (
      !source ||
      source.type !== "raster" ||
      source.locked ||
      !source.visible ||
      !target ||
      target.locked ||
      !target.visible
    )
      return false;
    if (!this.raster.canMergeDown(source.id, target.id)) {
      this.onInteractionError(
        "This tablet is low on drawing memory. Clear another layer before merging.",
      );
      return false;
    }
    if (!this.layers.delete(source.id)) return false;
    this.layers.activate(target.id);
    this.layers.setOpacity(target.id, 1);
    const after = this.layers.getSnapshot();
    this.layers.replace(before);
    this.executeCommand(
      createMergeLayerDownCommand(
        source.id,
        target.id,
        source.opacity,
        target.opacity,
        before,
        after,
      ),
    );
    return true;
  }

  public fitToView(): void {
    this.finishActiveInteraction();
    this.viewport.fit();
    this.reportZoom();
    this.requestRender();
  }

  public resetView(): void {
    this.finishActiveInteraction();
    this.viewport.reset();
    this.reportZoom();
    this.requestRender();
  }

  public panBy(delta: Point): void {
    this.viewport.panBy(delta);
    this.requestRender();
  }

  public zoomAt(anchor: Point, requestedZoom: number): void {
    this.viewport.zoomAt(anchor, requestedZoom);
    this.reportZoom();
    this.requestRender();
  }

  public applyGesture(
    start: ViewportTransform,
    startCentre: Point,
    currentCentre: Point,
    scaleRatio: number,
  ): void {
    this.viewport.applyGesture(start, startCentre, currentCentre, scaleRatio);
    this.reportZoom();
    this.requestRender();
  }

  public getViewportTransform(): ViewportTransform {
    return this.viewport.getTransform();
  }

  public destroy(): void {
    if (this.destroyed) return;
    this.interruptActiveInteraction("canvas-stage-destroy");
    this.destroyed = true;
    if (this.animationFrame !== null) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
    this.raster.destroy();
    this.baselineRaster.destroy();
    const strokeCanvas = this.strokeRenderer.getCanvas();
    strokeCanvas.width = 1;
    strokeCanvas.height = 1;
    this.releaseActiveLayerPreview();
    this.rasterSelection.clear();
    this.releaseRasterSelectionPreviewResources();
  }

  private beginRasterSelectionPointer(documentPoint: Point): boolean {
    if (!this.layers.canDraw()) {
      this.onInteractionError("Choose a visible, unlocked drawing layer before selecting art.");
      return false;
    }
    const activeLayerId = this.layers.getActive().id;
    const current = this.rasterSelection.getSelection();
    if (current && current.layerId !== activeLayerId) this.cancelRasterSelection(true);

    // Existing selection geometry is already constrained to its document or
    // comic panel. Test its bounded 44 CSS-pixel body/handle targets before
    // rejecting a new marquee point outside the page or in a gutter. This is
    // the only off-document input accepted by Select Art.
    const existingHit = this.rasterSelection.hitTest(
      documentPoint,
      this.viewport.getTransform().zoom,
    );
    if (existingHit) return this.beginRasterSelectionTransform(documentPoint, existingHit);
    if (!this.isInsideDocument(documentPoint)) return false;

    let clipRect = integerConstraintRect({
      x: 0,
      y: 0,
      width: this.documentSize.width,
      height: this.documentSize.height,
    });
    let panelId: string | undefined;
    if (this.comicDocument.getPanels().length > 0) {
      const touchedPanel = panelAtPoint(this.comicDocument.getPanels(), documentPoint);
      if (!touchedPanel) {
        this.cancelRasterSelection(true);
        return false;
      }
      const selectedPanel = this.comicDocument.getSelectedPanel();
      if (selectedPanel?.id !== touchedPanel.id) {
        this.cancelRasterSelection(true);
        this.comicDocument.selectPanel(touchedPanel.id);
        this.comicDocument.selectObject(null);
        this.reportSelection();
        this.requestRender();
        return false;
      }
      clipRect = integerConstraintRect(this.panelRect(touchedPanel));
      panelId = touchedPanel.id;
    }

    this.releaseRasterSelectionPreviewResources();
    this.rasterSelection.beginMarquee(activeLayerId, documentPoint, clipRect, panelId);
    this.reportRasterSelection();
    this.requestRender();
    return true;
  }

  private beginRasterSelectionTransform(
    documentPoint: Point,
    handle: Parameters<RasterSelectionController["beginTransform"]>[0],
  ): boolean {
    const selection = this.rasterSelection.getSelection();
    if (!selection || selection.layerId !== this.layers.getActive().id) return false;
    const regionBytes = selectionRegionBytes(selection.sourceRect);
    if (
      !selectionFitsMemoryLimits(selection.sourceRect) ||
      !this.raster.canAllocate(selection.layerId) ||
      !this.raster.canBeginSelectionWithPreview(selection.layerId, regionBytes)
    ) {
      this.onInteractionError(
        "This tablet is low on drawing memory. Try selecting a smaller area.",
      );
      return false;
    }

    // The shared budget preflight above occurs before the document-sized
    // preview, selected pixel capture, region canvas, or active controller state.
    try {
      this.ensureActiveLayerPreviewCanvas();
      const pixels = this.raster.captureSelection(selection.layerId, selection.sourceRect);
      if (!pixels?.hasVisiblePixels) {
        this.releaseRasterSelectionPreviewResources();
        this.onInteractionError("There are no drawn pixels inside that selection yet.");
        return false;
      }
      const sourceCanvas = createRasterPixelCanvas(pixels);
      this.activeRasterSelectionPixels = pixels;
      this.activeRasterSelectionSourceCanvas = sourceCanvas;
      if (
        !this.rasterSelection.beginTransform(
          handle,
          documentPoint,
          this.viewport.getTransform().zoom,
        )
      ) {
        this.releaseRasterSelectionPreviewResources();
        return false;
      }
      this.reportRasterSelection();
      this.requestRender();
      return true;
    } catch {
      this.releaseRasterSelectionPreviewResources();
      this.onInteractionError(
        "This tablet is low on drawing memory. Try selecting a smaller area.",
      );
      return false;
    }
  }

  private finishRasterSelectionInteraction(): void {
    const selection = this.rasterSelection.getSelection();
    const pixels = this.activeRasterSelectionPixels;
    const completed = this.rasterSelection.finishInteraction();
    this.releaseRasterSelectionPreviewResources();
    if (!completed) {
      this.reportRasterSelection();
      this.requestRender();
      return;
    }
    if (completed.kind === "marquee") {
      if (completed.rect && selectionFitsMemoryLimits(completed.rect)) {
        this.rasterSelection.setSelection({
          layerId: completed.layerId,
          sourceRect: completed.rect,
          clipRect: completed.clipRect,
          panelId: completed.panelId,
        });
      } else {
        this.rasterSelection.clear();
        if (completed.rect) {
          this.onInteractionError("That selection is too large. Try selecting a smaller area.");
        }
      }
      this.reportRasterSelection();
      this.requestRender();
      return;
    }
    if (!selection || !pixels?.hasVisiblePixels) {
      this.reportRasterSelection();
      this.requestRender();
      return;
    }
    const command = createRasterSelectionCommand({
      action: completed.action,
      layerId: selection.layerId,
      sourceRect: selection.sourceRect,
      sourcePixels: pixels,
      destination: completed.after,
      clearSource: true,
      clipRect: selection.clipRect,
    });
    this.executeCommand(command);
    this.rasterSelection.commitTransform(completed.after);
    this.reportRasterSelection();
    this.requestRender();
  }

  private commitRasterSelectionCommand(
    action: RasterSelectionAction,
    destination: RasterSelectionTransform | undefined,
    clearSource: boolean,
  ): boolean {
    if (this.rasterSelection.isActive()) return false;
    const selection = this.rasterSelection.getSelection();
    if (
      !selection ||
      selection.layerId !== this.layers.getActive().id ||
      !this.layers.canDraw() ||
      !selectionFitsMemoryLimits(selection.sourceRect)
    ) {
      return false;
    }
    const regionBytes = selectionRegionBytes(selection.sourceRect);
    if (!this.raster.canApplySelectionCommand(selection.layerId, regionBytes)) {
      this.onInteractionError(
        "This tablet is low on drawing memory. Try selecting a smaller area.",
      );
      return false;
    }
    try {
      const pixels = this.raster.captureSelection(selection.layerId, selection.sourceRect);
      if (!pixels?.hasVisiblePixels) {
        this.onInteractionError("There are no drawn pixels inside that selection yet.");
        return false;
      }
      const command = createRasterSelectionCommand({
        action,
        layerId: selection.layerId,
        sourceRect: selection.sourceRect,
        sourcePixels: pixels,
        destination,
        clearSource,
        clipRect: selection.clipRect,
      });
      this.executeCommand(command);
      if (destination) this.rasterSelection.commitTransform(destination);
      this.reportRasterSelection();
      this.requestRender();
      return true;
    } catch {
      this.onInteractionError("The selected art could not be changed safely. Please try again.");
      return false;
    }
  }

  private cancelRasterSelection(clearSelection: boolean): boolean {
    const hadInteraction = this.rasterSelection.cancelInteraction();
    const hadSelection = clearSelection ? this.rasterSelection.clear() : false;
    this.releaseRasterSelectionPreviewResources();
    if (hadInteraction || hadSelection) {
      this.reportRasterSelection();
      this.requestRender();
    }
    return hadInteraction || hadSelection;
  }

  private cancelActiveRasterSelectionInteraction(): boolean {
    if (!this.rasterSelection.isActive()) return false;
    this.rasterSelection.cancelInteraction();
    this.releaseRasterSelectionPreviewResources();
    this.reportRasterSelection();
    this.requestRender();
    return true;
  }

  private ensureActiveLayerPreviewCanvas(): HTMLCanvasElement {
    const canvas = this.activeLayerPreviewCanvas ?? document.createElement("canvas");
    this.activeLayerPreviewCanvas = canvas;
    if (canvas.width !== this.documentSize.width || canvas.height !== this.documentSize.height) {
      canvas.width = this.documentSize.width;
      canvas.height = this.documentSize.height;
    }
    if (!canvas.getContext("2d")) {
      throw new Error("Canvas 2D is unavailable for the active layer preview.");
    }
    return canvas;
  }

  private releaseRasterSelectionPreviewResources(): void {
    if (this.activeRasterSelectionSourceCanvas) {
      this.activeRasterSelectionSourceCanvas.width = 1;
      this.activeRasterSelectionSourceCanvas.height = 1;
      this.activeRasterSelectionSourceCanvas = null;
    }
    this.activeRasterSelectionPixels = null;
    this.releaseActiveLayerPreview();
  }

  private beginStrokeAtDocumentPoint(
    documentPoint: Point,
    pointerType: PointerKind,
    rawPressure: number,
    panel?: ComicPanel,
  ): boolean {
    this.smoothedPressure = normalizePressure(
      rawPressure,
      pointerType,
      this.settings.pressureEnabled,
    );
    const strokePoint = { ...documentPoint, pressure: this.smoothedPressure };
    this.activeStrokeSettings = {
      ...this.settings,
      tool: this.studioTool as DrawingSettings["tool"],
    };
    this.activeStrokePanel = panel ? { ...panel } : null;
    this.activeStrokeLayerId = this.layers.getActive().id;
    this.activeStrokeCapture.begin(strokePoint);
    this.strokeRenderer.begin(
      this.activeStrokeSettings,
      strokePoint,
      panel ? this.panelRect(panel) : undefined,
    );
    this.strokeActive = true;
    this.requestRender();
    return true;
  }

  private ensureEditableRasterLayer(): boolean {
    if (this.layers.canDraw()) return true;
    const compatible = this.layers
      .getSnapshot()
      .layers.find((layer) => layer.type === "raster" && layer.visible && !layer.locked);
    if (!compatible || !this.layers.activate(compatible.id)) return false;
    this.syncDocumentLayers();
    this.reportLayers();
    // Tool-driven activation is persistent session metadata but is not an
    // Undo/Redo command, matching established layer-selection behaviour.
    this.onDocumentChange("activeLayer");
    this.requestRender();
    return this.layers.canDraw();
  }

  private finishPendingRasterTap(): void {
    const pending = this.pendingRasterTap;
    this.pendingRasterTap = null;
    if (!pending) return;
    // Pick Colour and Fill are pointer-up tap operations. Never let an older
    // valid sample make an out-of-clip final pointer position executable.
    if (!pending.isPointerInsideClip) {
      if (pending.tool === "pickColour") {
        this.onInteractionError("There is no visible colour here to pick.");
      }
      return;
    }
    if (pending.tool === "pickColour") {
      const colour = this.sampleCompositeColour(pending.point);
      if (colour) this.onColourPick(colour);
      else this.onInteractionError("There is no visible colour here to pick.");
      this.requestRender();
      return;
    }
    const target = this.raster.samplePixel(pending.layerId, pending.point.x, pending.point.y);
    if (!target) return;
    const command = createFillCommand({
      layerId: pending.layerId,
      x: pending.point.x,
      y: pending.point.y,
      target,
      replacementColour: this.settings.colour,
      replacementOpacity: this.settings.opacity,
      tolerance: this.fillTolerance,
      clipRect: pending.clipRect,
      panelId: pending.panelId,
    });
    try {
      const result = this.raster.applyFillCommand(command);
      if (result.status === "rejected") {
        this.onInteractionError(
          "This area is too large to fill safely on this tablet. Try a smaller panel.",
        );
      } else if (result.status === "changed") {
        this.recordAppliedCommand(command);
      }
    } catch {
      this.onInteractionError("That area could not be filled safely. Please try again.");
    }
    this.requestRender();
  }

  private sampleCompositeColour(point: Readonly<Point>): string | null {
    const canvas = document.createElement("canvas");
    canvas.width = 1;
    canvas.height = 1;
    const context = canvas.getContext("2d", { willReadFrequently: true });
    if (!context) return null;
    context.translate(-Math.floor(point.x), -Math.floor(point.y));
    compositeLayerDocument(context, this.raster, this.getDocumentState(), this.documentSize, false);
    const data = context.getImageData(0, 0, 1, 1).data;
    const colour = rgbaToHex([data[0] ?? 0, data[1] ?? 0, data[2] ?? 0, data[3] ?? 0]);
    canvas.width = 1;
    canvas.height = 1;
    return colour;
  }

  private executeCommand(command: HistoryCommand): void {
    const result = this.applyHistoryCommand(
      this.raster,
      this.comicDocument.getObjects(),
      this.layers.getSnapshot(),
      command,
    );
    this.comicDocument.setObjects(result.objects);
    this.layers.replace(result.layerState);
    this.recordAppliedCommand(command);
  }

  private recordAppliedCommand(command: HistoryCommand): void {
    this.syncDocumentLayers();
    this.history.add(command);
    this.reportHistory();
    this.reportLayers();
    this.onDocumentChange(command.kind === "layerMutation" ? command.action : command.kind);
    this.requestRender();
  }

  private applyHistoryCommand(
    raster: LayerRasterStore,
    inputObjects: ReadonlyArray<ComicBubbleObject>,
    inputLayerState: Readonly<LayerControllerState>,
    command: HistoryCommand,
  ): { objects: ComicBubbleObject[]; layerState: LayerControllerState } {
    let objects = inputObjects.map(cloneObject);
    let layerState: LayerControllerState = {
      activeLayerId: inputLayerState.activeLayerId,
      layers: inputLayerState.layers.map((layer) => ({ ...layer })),
    };
    if (command.kind === "stroke") {
      this.applyStrokeCommand(raster, command);
      return { objects, layerState };
    }
    if (command.kind === "shape") {
      if (!raster.applyShapeCommand(command)) {
        throw new Error("The shape could not be replayed within the memory limit.");
      }
      return { objects, layerState };
    }
    if (command.kind === "fill") {
      if (raster.applyFillCommand(command).status === "rejected") {
        throw new Error("The fill could not be replayed within the memory limit.");
      }
      return { objects, layerState };
    }
    if (command.kind === "rasterSelection") {
      if (!raster.applySelectionCommand(command)) {
        throw new Error("The selected pixels could not be replayed within the memory limit.");
      }
      return { objects, layerState };
    }
    if (command.kind === "clearPanel") {
      const panel = this.panelById(command.panelId);
      if (panel) raster.clearPanel(panel);
      return {
        objects: objects.filter((object) => object.panelId !== command.panelId),
        layerState,
      };
    }
    if (command.kind === "duplicatePanel") {
      const source = this.panelById(command.sourcePanelId);
      const target = this.panelById(command.targetPanelId);
      if (source && target) raster.duplicatePanel(source, target);
      return {
        objects: [
          ...objects.filter((object) => object.panelId !== command.targetPanelId),
          ...command.duplicatedObjects.map(cloneObject),
        ],
        layerState,
      };
    }
    if (command.kind === "clearLayer") {
      raster.clearLayer(command.layerId);
      return { objects, layerState };
    }
    if (command.kind === "layerMutation") {
      layerState = {
        activeLayerId: command.after.activeLayerId,
        layers: command.after.layers.map((layer) => ({ ...layer })),
      };
      raster.syncLayers(layerState.layers);
      return { objects, layerState };
    }
    if (command.kind === "mergeLayerDown") {
      raster.mergeDown(
        command.sourceLayerId,
        command.targetLayerId,
        command.sourceOpacity,
        command.targetOpacity,
      );
      layerState = {
        activeLayerId: command.after.activeLayerId,
        layers: command.after.layers.map((layer) => ({ ...layer })),
      };
      raster.syncLayers(layerState.layers);
      return { objects, layerState };
    }
    if (command.kind === "addComicObject") {
      return {
        objects: [
          ...objects.filter((object) => object.id !== command.object.id),
          cloneObject(command.object),
        ],
        layerState,
      };
    }
    if (command.kind === "transformComicObject") {
      return {
        objects: objects.map((object) =>
          object.id === command.after.id ? cloneObject(command.after) : object,
        ),
        layerState,
      };
    }
    if (command.kind === "editComicText") {
      return {
        objects: objects.map((object) =>
          object.id === command.objectId
            ? { ...object, data: { ...object.data, text: command.afterText } }
            : object,
        ),
        layerState,
      };
    }
    if (command.kind === "deleteComicObject") {
      objects = objects.filter((object) => object.id !== command.object.id);
    }
    return { objects, layerState };
  }

  private applyStrokeCommand(raster: LayerRasterStore, command: StrokeCommand): void {
    const surface = raster.get(command.layerId);
    if (!surface) return;
    const context = raster.ensureContext(command.layerId);
    this.strokeRenderer.renderCommand(command);
    context.save();
    context.globalCompositeOperation =
      command.settings.tool === "eraser" ? "destination-out" : "source-over";
    context.drawImage(this.strokeRenderer.getCanvas(), 0, 0);
    context.restore();
    surface.markChanged();
    this.strokeRenderer.clear();
  }

  private bakeIntoBaseline(commands: ReadonlyArray<HistoryCommand>): void {
    let objects = this.baselineObjects;
    let layerState = this.baselineLayerState;
    for (const command of commands) {
      const result = this.applyHistoryCommand(this.baselineRaster, objects, layerState, command);
      objects = result.objects;
      layerState = result.layerState;
    }
    this.baselineObjects = objects.map(cloneObject);
    this.baselineLayerState = {
      activeLayerId: layerState.activeLayerId,
      layers: layerState.layers.map((layer) => ({ ...layer })),
    };
  }

  private rebuildDocument(): void {
    const selectedLayerId = this.layers.getSnapshot().activeLayerId;
    this.raster.replaceWith(this.baselineRaster);
    let objects = this.baselineObjects.map(cloneObject);
    let layerState: LayerControllerState = {
      activeLayerId: this.baselineLayerState.activeLayerId,
      layers: this.baselineLayerState.layers.map((layer) => ({ ...layer })),
    };
    for (const command of this.history.getAppliedCommands()) {
      const result = this.applyHistoryCommand(this.raster, objects, layerState, command);
      objects = result.objects;
      layerState = result.layerState;
    }
    if (layerState.layers.some((layer) => layer.id === selectedLayerId)) {
      layerState.activeLayerId = selectedLayerId;
    }
    this.comicDocument.setObjects(objects);
    this.layers.replace(layerState);
    this.syncDocumentLayers();
    this.reportLayers();
    this.requestRender();
  }

  private mutateLayers(
    action: Parameters<typeof createLayerMutationCommand>[0],
    mutation: () => boolean,
  ): boolean {
    this.cancelRasterSelection(true);
    this.finishActiveInteraction();
    const before = this.layers.getSnapshot();
    if (!mutation()) return false;
    const after = this.layers.getSnapshot();
    this.layers.replace(before);
    this.executeCommand(createLayerMutationCommand(action, before, after));
    return true;
  }

  private syncDocumentLayers(): void {
    const state = this.layers.getSnapshot();
    this.comicDocument.setLayerState(state.layers, state.activeLayerId);
    this.raster.syncLayers(state.layers);
  }

  private getDocumentState(): PersistedDocumentState {
    this.syncDocumentLayers();
    return this.comicDocument.getSnapshot();
  }

  private reportZoom(): void {
    const transform = this.viewport.getTransform();
    this.publishViewportMetadata(transform);
    const zoomPercent = Math.round(transform.zoom * 100);
    if (zoomPercent === this.lastReportedZoom) return;
    this.lastReportedZoom = zoomPercent;
    this.onZoomChange(zoomPercent);
  }

  private reportHistory(): void {
    this.onHistoryChange(this.history.getState());
  }

  private reportSelection(): void {
    this.onSelectionChange(this.comicDocument.getSelection(this.comicInteraction.getPreview()));
  }

  private reportRasterSelection(): void {
    this.onRasterSelectionChange(this.rasterSelection.getUiState());
  }

  private reportLayers(): void {
    this.onLayerChange(this.layers.getSnapshot());
  }

  private requestRender(): void {
    if (this.destroyed || this.animationFrame !== null) return;
    this.animationFrame = requestAnimationFrame(() => {
      this.animationFrame = null;
      this.render();
    });
  }

  private render(): void {
    if (this.destroyed) return;
    const context = this.displayCanvas.getContext("2d");
    if (!context) return;
    const { zoom, panX, panY } = this.viewport.getTransform();
    this.publishViewportMetadata({ zoom, panX, panY });
    const dpr = this.displayDpr;

    context.save();
    context.setTransform(dpr, 0, 0, dpr, 0, 0);
    context.clearRect(0, 0, this.displayWidth, this.displayHeight);
    context.fillStyle = "#dfe4e8";
    context.fillRect(0, 0, this.displayWidth, this.displayHeight);
    context.restore();

    context.save();
    context.setTransform(dpr * zoom, 0, 0, dpr * zoom, dpr * panX, dpr * panY);
    context.shadowColor = "rgba(29, 39, 51, 0.22)";
    context.shadowBlur = 18 / Math.max(zoom, 0.05);
    context.shadowOffsetY = 5 / Math.max(zoom, 0.05);
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, this.documentSize.width, this.documentSize.height);
    context.shadowColor = "transparent";
    context.imageSmoothingEnabled = true;
    // Guides are constant-memory display vectors beneath every committed and
    // preview artwork layer. They are never passed to compositing or export.
    renderGuideOverlay(context, this.guideOverlay, {
      width: this.documentSize.width,
      height: this.documentSize.height,
      zoom,
    });
    const state = this.getDocumentState();
    const preview = this.comicInteraction.getPreview();
    if (preview) {
      state.objects = state.objects.map((object) => (object.id === preview.id ? preview : object));
    }
    const activeLayerPreview = this.prepareActiveLayerPreview();
    const selectionLayerId = this.rasterSelection.isTransformActive()
      ? this.rasterSelection.getSelection()?.layerId
      : undefined;
    const activePreviewLayerId =
      this.shapeInteraction.getPreview()?.layerId ?? this.activeStrokeLayerId ?? selectionLayerId;
    const rasterOverrides =
      activeLayerPreview && activePreviewLayerId
        ? new Map<string, CanvasImageSource>([[activePreviewLayerId, activeLayerPreview]])
        : undefined;
    // Live marks are isolated to a temporary active-layer surface first. The
    // normal compositor then runs exactly once, preserving opacity and strict
    // lower/active/upper ordering for Pencil through Eraser alike.
    compositeLayerDocument(context, this.raster, state, this.documentSize, false, rasterOverrides);

    context.globalCompositeOperation = "source-over";
    if (state.panels.length > 0) {
      renderPanelBoundaries(
        context,
        state.panels,
        this.documentSize.width,
        this.documentSize.height,
      );
    }
    renderComicSelection(context, this.comicDocument.getSelection(preview), state.panels, zoom);
    if (this.studioTool === "selectRasterArt") {
      renderRasterSelectionOverlay(context, this.rasterSelection.getOverlayState(), zoom);
    }
    context.strokeStyle = "rgba(53, 63, 72, 0.45)";
    context.lineWidth = 1.5 / Math.max(zoom, 0.05);
    context.strokeRect(0, 0, this.documentSize.width, this.documentSize.height);
    context.restore();
  }

  private panelById(panelId: string): ComicPanel | undefined {
    return this.comicDocument.getPanels().find((panel) => panel.id === panelId);
  }

  private prepareActiveLayerPreview(): HTMLCanvasElement | null {
    const selection = this.rasterSelection.getSelection();
    const selectionTransform = this.rasterSelection.isTransformActive()
      ? this.rasterSelection.getTransform()
      : null;
    if (
      selection &&
      selectionTransform &&
      this.activeRasterSelectionSourceCanvas &&
      this.activeRasterSelectionPixels
    ) {
      const canvas = this.ensureActiveLayerPreviewCanvas();
      const context = canvas.getContext("2d");
      if (!context) return null;
      context.save();
      context.setTransform(1, 0, 0, 1, 0, 0);
      context.globalAlpha = 1;
      context.globalCompositeOperation = "source-over";
      context.clearRect(0, 0, canvas.width, canvas.height);
      const committed = this.raster.get(selection.layerId)?.getCanvas();
      if (committed) context.drawImage(committed, 0, 0);
      context.beginPath();
      context.rect(
        selection.clipRect.x,
        selection.clipRect.y,
        selection.clipRect.width,
        selection.clipRect.height,
      );
      context.clip();
      context.clearRect(
        selection.sourceRect.x,
        selection.sourceRect.y,
        selection.sourceRect.width,
        selection.sourceRect.height,
      );
      drawRasterSelectionSource(
        context,
        this.activeRasterSelectionSourceCanvas,
        selectionTransform,
      );
      context.restore();
      return canvas;
    }

    const shape = this.shapeInteraction.getPreview();
    if (shape) {
      const canvas = this.ensureActiveLayerPreviewCanvas();
      const context = canvas.getContext("2d");
      if (!context) return null;
      context.save();
      context.setTransform(1, 0, 0, 1, 0, 0);
      context.globalAlpha = 1;
      context.globalCompositeOperation = "source-over";
      context.clearRect(0, 0, canvas.width, canvas.height);
      const committed = this.raster.get(shape.layerId)?.getCanvas();
      if (committed) context.drawImage(committed, 0, 0);
      renderShape(context, shape);
      context.restore();
      return canvas;
    }

    if (!this.strokeActive || !this.activeStrokeLayerId || !this.strokeRenderer.activeTool) {
      return null;
    }
    const canvas = this.ensureActiveLayerPreviewCanvas();
    const context = canvas.getContext("2d");
    if (!context) return null;
    context.save();
    context.setTransform(1, 0, 0, 1, 0, 0);
    context.globalAlpha = 1;
    context.globalCompositeOperation = "source-over";
    context.clearRect(0, 0, canvas.width, canvas.height);
    const committed = this.raster.get(this.activeStrokeLayerId)?.getCanvas();
    if (committed) context.drawImage(committed, 0, 0);
    context.globalCompositeOperation =
      this.strokeRenderer.activeTool === "eraser" ? "destination-out" : "source-over";
    context.drawImage(this.strokeRenderer.getCanvas(), 0, 0);
    context.restore();
    return canvas;
  }

  private releaseActiveLayerPreview(): void {
    if (!this.activeLayerPreviewCanvas) return;
    this.activeLayerPreviewCanvas.width = 1;
    this.activeLayerPreviewCanvas.height = 1;
    this.activeLayerPreviewCanvas = null;
  }

  private panelRect(panel: ComicPanel): Rect {
    return { x: panel.x, y: panel.y, width: panel.width, height: panel.height };
  }

  private isInsideDocument(point: Point): boolean {
    return (
      point.x >= 0 &&
      point.y >= 0 &&
      point.x <= this.documentSize.width &&
      point.y <= this.documentSize.height
    );
  }

  private publishViewportMetadata(transform: Readonly<ViewportTransform>): void {
    // Exact non-sensitive geometry keeps deterministic canvas pixel tests from
    // depending on the rounded child-facing zoom label.
    this.displayCanvas.dataset.viewportZoom = String(transform.zoom);
    this.displayCanvas.dataset.viewportPanX = String(transform.panX);
    this.displayCanvas.dataset.viewportPanY = String(transform.panY);
  }
}
